// src/App.js
import React, { useEffect, useState } from 'react'
import { Routes, Route } from 'react-router-dom'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { CssBaseline, Box } from '@mui/material'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

// Contexts
import { ThemeProvider as CustomThemeProvider } from './contexts/ThemeContext'
import { LanguageProvider } from './contexts/LanguageContext'
import { NotificationProvider } from './contexts/NotificationContext'

// Components
import AnimatedLoader from './components/common/AnimatedLoader'
import ScrollToTop from './components/common/ScrollToTop'
import Navigation from './components/layout/Navigation'
import Footer from './components/layout/Footer'
import AnimatedBackground from './components/common/AnimatedBackground'
import IdleSessionGuard from './components/common/IdleSessionGuard'
import RouteSeo from './components/seo/RouteSeo'

// Pages
import Home from './pages/Home'
import Login from './pages/Login'
import Register from './pages/Register'
import ForgotPassword from './pages/ForgotPassword'
import Dashboard from './pages/Dashboard'
import SendMoney from './pages/SendMoney'
import Tickets from './pages/Tickets'
import Wallet from './pages/Wallet'
import Transactions from './pages/Transactions'
import Notifications from './pages/Notifications'
import Profile from './pages/Profile'
import Settings from './pages/Settings'
import HelpCenter from './pages/HelpCenter'
import ContactUs from './pages/ContactUs'
import FAQ from './pages/FAQ'
import StatusPage from './pages/StatusPage'
import About from './pages/About'
import Careers from './pages/Careers'
import Press from './pages/Press'
import Blog from './pages/Blog'
import BlogPost from './pages/BlogPost'
import PrivacyPolicy from './pages/legal/PrivacyPolicy'
import TermsOfService from './pages/legal/TermsOfService'
import Compliance from './pages/legal/Compliance'
import KYCPOLICY from './pages/legal/KYCPOLICY'
import RegulatoryInfo from './pages/legal/RegulatoryInfo'
import NotFound from './pages/NotFound'

// ✅ NEW PAYMENT STRUCTURE
import PaymentServices from './pages/PaymentServices'
import PaymentAmount from './pages/PaymentAmount'
import PaymentConfirm from './pages/PaymentConfirm'
import BankTransfers from './pages/BankTransfers'

import PayBills from './pages/PayBills'
import Donations from './pages/Donations'
import Contributions from './pages/Contributions'
import ContributeGroup from './pages/ContributeGroup'
import ReceiptShare from './pages/ReceiptShare'

import ProtectedRoute from './components/common/ProtectedRoute'
import MerchantInvoices from './pages/merchant/MerchantInvoices'
import MerchantInvoiceCreate from './pages/merchant/MerchantInvoiceCreate'
import MerchantInvoiceDetail from './pages/merchant/MerchantInvoiceDetail'
import MerchantScanTicket from './pages/merchant/MerchantScanTicket'
import MerchantPosMode from './pages/merchant/MerchantPosMode'
import MerchantReceiveQr from './pages/merchant/MerchantReceiveQr'
import MerchantCreateEvent from './pages/merchant/MerchantCreateEvent'
import MerchantSellAirtime from './pages/merchant/MerchantSellAirtime'
import MerchantBusinessTax from './pages/merchant/MerchantBusinessTax'
import MerchantPayrollWorkforce from './pages/merchant/MerchantPayrollWorkforce'
import GovernmentDashboard from './pages/government/GovernmentDashboard'
import GovernmentNotifications from './pages/government/GovernmentNotifications'
import PublicInvoicePay from './pages/public/PublicInvoicePay'

import './styles/globals.css'
import './styles/animations.css'

const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#FF8A00' },
        secondary: { main: '#1A1F2E' },
        background: {
            default: '#080C14',
            paper: '#0B1020',
        },
        success: { main: '#FF8A00' },
        error: { main: '#f87171' },
        text: {
            primary: '#F3F4F6',
            secondary: '#A1A1AA',
        },
    },
})

const App = () => {
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const timer = setTimeout(() => setLoading(false), 900)
        return () => clearTimeout(timer)
    }, [])

    if (loading) return <AnimatedLoader />

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />

            <CustomThemeProvider>
                <LanguageProvider>
                    <NotificationProvider>
                        <ScrollToTop />
                        <RouteSeo />
                        <IdleSessionGuard />

                        <Box sx={{ position: 'relative', minHeight: '100vh' }}>

                            {/* Background */}
                            <Box
                                sx={{
                                    position: 'fixed',
                                    inset: 0,
                                    zIndex: 0,
                                    pointerEvents: 'none',
                                }}
                            >
                                <AnimatedBackground />
                            </Box>

                            {/* Content */}
                            <Box sx={{ position: 'relative', zIndex: 1 }}>
                                <Navigation />

                                <main>
                                    <Routes>

                                        {/* Public */}
                                        <Route path="/" element={<Home />} />
                                        <Route path="/login" element={<Login />} />
                                        <Route path="/register" element={<Register />} />
                                        <Route path="/forgot-password" element={<ForgotPassword />} />

                                        <Route path="/help-center" element={<HelpCenter />} />
                                        <Route path="/contact" element={<ContactUs />} />
                                        <Route path="/faq" element={<FAQ />} />
                                        <Route path="/status" element={<StatusPage />} />
                                        <Route path="/about" element={<About />} />
                                        <Route path="/careers" element={<Careers />} />
                                        <Route path="/press" element={<Press />} />
                                        <Route path="/blog" element={<Blog />} />
                                        <Route path="/blog/:slug" element={<BlogPost />} />

                                        <Route path="/legal/privacy" element={<PrivacyPolicy />} />
                                        <Route path="/legal/terms" element={<TermsOfService />} />
                                        <Route path="/legal/compliance" element={<Compliance />} />
                                        <Route path="/legal/kyc" element={<KYCPOLICY />} />
                                        <Route path="/legal/regulatory" element={<RegulatoryInfo />} />

                                        <Route path="/pay/invoice/:token" element={<PublicInvoicePay />} />
                                        <Route path="/invoice/:token" element={<PublicInvoicePay />} />
                                        <Route path="/receipt/:id" element={<ReceiptShare />} />
                                        <Route path="/contribute/:groupId" element={<ContributeGroup />} />

                                        {/* ✅ PAYMENT FLOW */}
                                        <Route path="/payment" element={<PaymentServices />} />
                                        <Route path="/payment/amount" element={<PaymentAmount />} />
                                        <Route path="/payment/confirm" element={<PaymentConfirm />} />
                                        <Route path="/pay-bills" element={<PayBills />} />
                                        <Route path="/bank-transfers" element={<BankTransfers />} />

                                        {/* Protected */}
                                        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                                        <Route path="/send" element={<ProtectedRoute><SendMoney /></ProtectedRoute>} />
                                        <Route path="/tickets" element={<ProtectedRoute><Tickets /></ProtectedRoute>} />
                                        <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
                                        <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
                                        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                                        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                                        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                                        <Route path="/donations" element={<ProtectedRoute><Donations /></ProtectedRoute>} />
                                        <Route path="/contributions" element={<ProtectedRoute><Contributions /></ProtectedRoute>} />
                                        <Route path="/invoices" element={<ProtectedRoute><MerchantInvoices /></ProtectedRoute>} />
                                        <Route path="/invoices/create" element={<ProtectedRoute><MerchantInvoiceCreate /></ProtectedRoute>} />
                                        <Route path="/invoices/:id" element={<ProtectedRoute><MerchantInvoiceDetail /></ProtectedRoute>} />
                                        <Route path="/merchant/scan-ticket" element={<ProtectedRoute><MerchantScanTicket /></ProtectedRoute>} />
                                        <Route path="/merchant/pos" element={<ProtectedRoute><MerchantPosMode /></ProtectedRoute>} />
                                        <Route path="/merchant/receive-qr" element={<ProtectedRoute><MerchantReceiveQr /></ProtectedRoute>} />
                                        <Route path="/merchant/create-event" element={<ProtectedRoute><MerchantCreateEvent /></ProtectedRoute>} />
                                        <Route path="/merchant/sell-airtime" element={<ProtectedRoute><MerchantSellAirtime /></ProtectedRoute>} />
                                        <Route path="/merchant/business-tax" element={<ProtectedRoute><MerchantBusinessTax /></ProtectedRoute>} />
                                        <Route path="/merchant/payroll-workforce" element={<ProtectedRoute><MerchantPayrollWorkforce /></ProtectedRoute>} />
                                        <Route path="/government/dashboard" element={<ProtectedRoute><GovernmentDashboard /></ProtectedRoute>} />
                                        <Route path="/government/notifications" element={<ProtectedRoute><GovernmentNotifications /></ProtectedRoute>} />

                                        <Route path="*" element={<NotFound />} />
                                    </Routes>
                                </main>

                                <Footer />
                            </Box>
                        </Box>

                        <ToastContainer theme="dark" />
                    </NotificationProvider>
                </LanguageProvider>
            </CustomThemeProvider>
        </ThemeProvider>
    )
}

export default App

